import { Employee } from '../../types/Employee/Employee'
import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { WorkItem } from '../../types/Work/WorkItem'
import { WorkItemTask } from '../../types/Work/WorkItemTask'
import { ReworkTask } from '../../types/Work/WorkItemTask'
import { Constraint } from '../../types/Work/Constraint'
import { showErrorToast } from '../CommonServices/alertServices'
import {
	ReworkAssignmentCache,
	TaskAssignmentCache,
	WorkItemAssignmentCache,
} from '../../types/Cache/ForemanCache'
const traverseWorkItems = (
	workItems: WorkItem[],
	allWorkItems: WorkItem[],
	allTasks: WorkItemTask[],
	allReworks: ReworkTask[],
	allConstraints: Constraint[],
) => {
	for (const workItem of workItems) {
		const cleanWorkItem: WorkItem = {
			...workItem,
			childrenWorkItems: [],
			tasks: [],
			reworks: [],
			constraints: [],
		}

		allWorkItems.push(cleanWorkItem)

		if (workItem.tasks && workItem.tasks.length > 0) {
			for (const task of workItem.tasks) {
				const cleanTask: WorkItemTask = {
					...task,
					workItemId: workItem.id,
					reworks: null,
				}
				allTasks.push(cleanTask)

				if (task.reworks && task.reworks.length > 0) {
					for (const rework of task.reworks) {
						const cleanRework: ReworkTask = {
							...rework,
							workItemTaskId: task.id,
							workItemId: workItem.id,
						}
						allReworks.push(cleanRework)
					}
				}
			}
		}

		if (workItem.constraints && workItem.constraints.length > 0) {
			for (const constraint of workItem.constraints) {
				const cleanConstraint: Constraint = {
					...constraint,
					workItemId: workItem.id,
				}
				allConstraints.push(cleanConstraint)
			}
		}

		if (workItem.childrenWorkItems && workItem.childrenWorkItems.length > 0) {
			traverseWorkItems(
				workItem.childrenWorkItems,
				allWorkItems,
				allTasks,
				allReworks,
				allConstraints,
			)
		}
	}
}
export const mapForemanWorkItems = async (db: ForemanDB, foreman: Employee) => {
	const allWorkItems: WorkItem[] = []
	const allTasks: WorkItemTask[] = []
	const allReworks: ReworkTask[] = []
	const allConstraints: Constraint[] = []

	if (foreman.workItems && foreman.workItems.length > 0) {
		traverseWorkItems(foreman.workItems, allWorkItems, allTasks, allReworks, allConstraints)
	}

	try {
		await db.transaction(
			'rw',
			[db.workItems, db.tasks, db.reworks, db.constraints, db.employee],
			async () => {
				db.setHooksEnabled(false)
				await db.workItems.clear()
				await db.tasks.clear()
				await db.reworks.clear()
				await db.constraints.clear()

				if (allWorkItems.length > 0) {
					await db.workItems.bulkAdd(allWorkItems)
				}

				if (allTasks.length > 0) {
					await db.tasks.bulkAdd(allTasks)
				}

				if (allReworks.length > 0) {
					await db.reworks.bulkAdd(allReworks)
				}

				if (allConstraints.length > 0) {
					await db.constraints.bulkAdd(allConstraints)
				}

				const cleanForeman: Employee = {
					...foreman,
					workItems: [],
				}

				await db.employee.clear()
				await db.employee.add(cleanForeman)
				db.setHooksEnabled(true)
			},
		)
	} catch (error) {
		showErrorToast({
			message: 'Error mapping foreman work items',
		})
		throw error
	}
}

export const mapLaborWorkItems = async (db: ForemanDB, labors: Employee[]) => {
	const workItemAssignments: WorkItemAssignmentCache[] = []
	const taskAssignments: TaskAssignmentCache[] = []
	const reworkAssignments: ReworkAssignmentCache[] = []
	// Extract entities and create assignments in a single loop
	for (const labor of labors) {
		if (labor.workItems && labor.workItems.length > 0) {
			const laborWorkItems: WorkItem[] = []
			const laborTasks: WorkItemTask[] = []
			const laborReworks: ReworkTask[] = []
			const laborConstraints: Constraint[] = []

			// Extract entities for this labor
			traverseWorkItems(labor.workItems, laborWorkItems, laborTasks, laborReworks, laborConstraints)
			// Create work item assignments
			for (const workItem of laborWorkItems) {
				workItemAssignments.push({
					workerId: labor.id,
					workItemId: workItem.id,
				})
			}

			// Create task assignments for this labor
			for (const task of laborTasks) {
				taskAssignments.push({
					workerId: labor.id,
					workItemTaskId: task.id,
				})
				if (task.status === 'Active') {
					labor.activeTaskId = task.id
				}
			}

			// Create rework assignments for this labor
			for (const rework of laborReworks) {
				reworkAssignments.push({
					workerId: labor.id,
					reworkId: rework.id,
				})
				if (rework.status === 'Active') {
					labor.activeReworkId = rework.id
				}
			}
		}
	}

	try {
		await db.transaction(
			'rw',
			[db.directReports, db.workItemAssignments, db.taskAssignments, db.reworkAssignments],
			async () => {
				db.setHooksEnabled(false)
				await db.directReports.clear()
				await db.workItemAssignments.clear()
				await db.taskAssignments.clear()
				await db.reworkAssignments.clear()

				const cleanLabors = labors.map((labor) => ({
					...labor,
					workItems: [],
				}))
				if (cleanLabors.length > 0) {
					await db.directReports.bulkAdd(cleanLabors)
				}

				if (workItemAssignments.length > 0) {
					await db.workItemAssignments.bulkAdd(workItemAssignments)
				}

				if (taskAssignments.length > 0) {
					await db.taskAssignments.bulkAdd(taskAssignments)
				}

				if (reworkAssignments.length > 0) {
					await db.reworkAssignments.bulkAdd(reworkAssignments)
				}
				db.setHooksEnabled(true)
			},
		)
	} catch (error) {
		showErrorToast({
			message: 'Error mapping labor work items',
		})
		throw error
	}
}
